import ContextineAPI from 'clients/ContextineAPI';
import { AppDispatcher } from 'dispatcher';
import { jwtDecode } from 'jwt-decode';
import isNil from 'lodash/isNil';
import moment from 'moment';
import StorageAPI from 'src/core/utils/StorageAPI';
import {
  CONTEXTINE_CREDS,
  LOG_IN_STATUS,
  SET_CONTEXTINE_CREDS,
  STORE_QUERY,
  USER_LOGGED_IN,
} from 'vd-constants';
import { BaseStore } from './BaseStore';
/* UTILS */
import { type ILocationCustomer } from '@solink/vd-data-layer/api-clients/callhome/types';
import { type JWTUser } from '@solinkcorp/authentication';
import { CallhomeApi } from 'clients';
import AuthService from 'models/AuthService';
import FeaturesUtil from 'utils/FeaturesUtil';
import PrefsUtil from 'utils/PrefsUtil';
import { type RouteQueryProps } from 'utils/RouteUtil';

const IS_SOLINK_USER = /@(solinkcorp|solink).com$/;
const IS_CUSTOMER_COM = /int@customer.com/;

let _user: JWTUser | undefined;
let _metadata: {
  // TODO: Might be other fields in here.
  hd?: boolean;
  playbackSource?: string;
} = {};
let _originalQuery: RouteQueryProps = {};
let _contextineCreds: { token: string; refreshToken: string } | undefined;
let _contextineRefreshTimeout: any;
let _customer: ILocationCustomer = {
  name: '',
  id: '',
  cloudId: '',
  resellerId: '',
};

function getJwtTokenExpiry(jwt: string) {
  const decoded = jwtDecode(jwt);
  return (decoded as any).exp * 1000;
}

function refreshContextine(refreshToken: string) {
  const contextineApi = new ContextineAPI();
  return contextineApi
    .refresh(refreshToken)
    .then((res) => {
      if (res.access_token) {
        const contextineCreds = {
          token: res.access_token,
          refreshToken,
        };

        StorageAPI.storeLocally(CONTEXTINE_CREDS, contextineCreds);
        _contextineCreds = contextineCreds;

        const expirationTime = getJwtTokenExpiry(res.access_token);
        setNextContextineRefresh(expirationTime, refreshToken);

        AuthStore.emitChange();
      } else {
        console.error('Received error response while refreshing Contextine', res);
        throw Error(res);
      }
    })
    .catch((err) => {
      console.error(err);
    });
}

function setNextContextineRefresh(exp: number | Date, refreshToken: string) {
  clearTimeout(_contextineRefreshTimeout);

  const expiry = moment(exp);
  const currentTime = moment(new Date());

  let timeUntilRefresh;
  const secondsUntilExpiry = expiry.diff(currentTime, 'seconds');
  if (secondsUntilExpiry < 300) {
    timeUntilRefresh = 1;
  } else {
    timeUntilRefresh = 1000 * (secondsUntilExpiry - 300);
  }

  _contextineRefreshTimeout = setTimeout(() => {
    refreshContextine(refreshToken);
  }, timeUntilRefresh);
}

export const AuthStore = {
  ...BaseStore,
  getUser: function () {
    return AuthService.getSession()?.authTokenPayload || null;
  },

  getCustomer: () => {
    return _customer;
  },

  isImpersonating: function () {
    return AuthService.isImpersonating;
  },

  getJwt: function () {
    return AuthService.getSession()?.authToken;
  },

  /**
   * @deprecated - use `useUserSession().s3` instead.
   */
  getS3: function () {
    return AuthService.getSession()?.s3;
  },

  getEula: () => AuthService.getSession()?.eula,

  getOriginalQuery: function () {
    return _originalQuery;
  },

  getContextine: function () {
    if (!_contextineCreds) {
      const credsString = StorageAPI.getLocally<any>(CONTEXTINE_CREDS);
      if (credsString) {
        try {
          _contextineCreds = credsString;
        } catch (e) {
          console.error('Cannot parse CONTEXTINE_CREDS from local storage. Resetting', e);
          _contextineCreds = undefined;
        }
      } else {
        _contextineCreds = undefined;
      }
    }

    return _contextineCreds;
  },

  canViewHD: function () {
    const hdAccess = _metadata.hd;

    // Default to true when hd is undefined or set to null
    return isNil(hdAccess) ? true : hdAccess;
  },

  isTestUser: function (): boolean {
    if (_user && AuthService.isImpersonating) {
      const email = AuthService.impersonator?.authTokenPayload?.email;

      return !!email && IS_SOLINK_USER.test(email);
    }
    return !!_user && (IS_CUSTOMER_COM.test(_user.email) || IS_SOLINK_USER.test(_user.email));
  },

  isLoggedIn: function () {
    return AuthService.isLoggedIn && _user;
  },

  dispatchToken: AppDispatcher.register((action: any) => {
    switch (action.type) {
      case USER_LOGGED_IN: {
        if (action.token) {
          _user = jwtDecode(action.token);

          CallhomeApi()
            .getCustomer()
            .then((res) => {
              _customer = res;
              AuthStore.emitChange();
            })
            .catch((err) => {
              console.error('Error retrieving customer', err);
            });
        } else if (action.impersonator) {
          _user = undefined;
          _metadata = {};
        }

        let userFeatures = undefined;
        let userPref = undefined;
        if (_user) {
          if (action.chUserMetadata) {
            _metadata = action.chUserMetadata || {};
          }

          userFeatures = action.chUserFeatures;
          userPref = action.chUserPreferences;
        }
        FeaturesUtil.updateUserFeatures(userFeatures);
        PrefsUtil.updatePrefs(userPref);

        AuthStore.emitChange();

        break;
      }

      case LOG_IN_STATUS:
        break;

      case STORE_QUERY:
        _originalQuery = action.payload || {};
        AuthStore.emitChange();
        break;

      case SET_CONTEXTINE_CREDS: {
        const creds = action.creds;
        StorageAPI.storeLocally(CONTEXTINE_CREDS, creds);
        _contextineCreds = creds;

        if (creds) {
          const expirationTime = getJwtTokenExpiry(creds.token);
          setNextContextineRefresh(expirationTime, creds.refreshToken);
        }

        AuthStore.emitChange();
        break;
      }
    }

    return true;
  }),
};

// on load, set refresh timeout
const contextine = AuthStore.getContextine();
if (contextine) {
  const expirationTime = getJwtTokenExpiry(contextine.token);
  setNextContextineRefresh(expirationTime, contextine.refreshToken);
}
